import {AttrsSchema, Component} from 'panel';

import {serializeAttributes} from 'common/util';
import {ButtonTheme} from 'common/components/button/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {ContextPanelData} from '../context-panel/types';
import {template} from './template';
import css from './index.styl?inline';
import {ChartHeaderAttrs, ControlBarState} from './types';

export class ControlBar extends Component<ControlBarState, ChartHeaderAttrs> {
  static get attrsSchema(): AttrsSchema<ChartHeaderAttrs> {
    return {
      ...super.attrsSchema,
      controls: {
        type: `json`,
        description: `interface: Section`,
      },
      theme: {
        type: `string`,
        description: `Theme applied to untruncated buttons`,
        default: `tertiary`,
        enum: Object.values(ButtonTheme),
      },
      'overflow-controls': {
        type: `json`,
        default: [],
        description: `interface: Section[]`,
      },
      'overflow-icon': {
        type: `string`,
        description: `Custom overflow icon for the mp-select dropdown button`,
        default: `hor-ellipsis`,
      },
      'overflow-menu-width': {
        type: `number`,
        default: 160,
      },
      disabled: `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        menuOpen: false,
        contextPanelTransform: null,
      },
      helpers: {
        openMenu: () => this.update({menuOpen: true}),
        onDropMenuStateChange: (ev) => this.update({menuOpen: ev.detail.state === `open`}),
        getOverflowItems: () => {
          const items = this.attr(`overflow-controls`);
          const totalItems = items.reduce((a, section) => a + section.items.length, 0);

          if (totalItems < 1) {
            return [];
          }

          return items;
        },

        getContextualOverflowTooltip: () => {
          const labels = this.helpers
            .getOverflowItems()
            .reduce((a, b) => [...a, ...b.items], [])
            .map((item) => item.shortLabel || item.label)
            .filter(Boolean);

          if (!labels.length) {
            return null;
          }

          return {
            text: `${labels.slice(0, 2).join(`, `)}${labels.length > 2 ? `, and more...` : ``}`,
          };
        },

        getSelectAttrs: () => {
          return serializeAttributes({
            theme: ButtonTheme.Tertiary,
            tooltip: this.helpers.getContextualOverflowTooltip(),
            icon: {
              leftIcon: this.attr(`overflow-icon`),
            },
            'icon-only': true,
            sections: this.helpers.getOverflowItems(),
            'menu-width': this.attr(`overflow-menu-width`),
            elref: `overflowMenu`,
            disabled: this.attr(`disabled`),
          });
        },

        handleMenuClick: (ev) => {
          const {value} = ev.detail.item;

          this.dispatchEvent(new CustomEvent(`select`, {detail: {value}}));
          this.update({menuOpen: false});
        },

        handleClick: (ev) => {
          const value = ev.target.getAttribute(`value`);
          this.selectOption(value);
        },
      },
    };
  }

  public selectOption(value) {
    this.dispatchEvent(new CustomEvent(`select`, {detail: {value}}));
  }

  /** @param value - function that returns context panel data to be shown for focused item */
  set contextPanelTransform(value: (item: any) => ContextPanelData) {
    this.update({contextPanelTransform: value});
  }
}

export const ControlBarEl = defineAndMakeJsxFactory(`mp-control-bar`, ControlBar);
